import type { ILegalConsentRepository } from './interfaces/ILegalConsentRepository'
import type { CurrentLegalVersion, UserLegalState } from '@/types'
import { getDocumentFromServer, type FirestoreDocument } from '@/services/firestore'

interface LegalDocsRaw extends FirestoreDocument {
  version_no?: unknown
  description?: unknown
}

interface UserLegalRaw extends FirestoreDocument {
  accepted_legal_version?: unknown
  display_name?: unknown
}

/**
 * Las dos lecturas van **contra el servidor** (`getDocumentFromServer`), no por
 * `getDocument`: deciden si se muestra un modal que bloquea la sesión, y una
 * respuesta de caché vacía se leería como «el usuario no existe» o «no hay
 * versión» sin ningún error. Si el servidor no responde, la promesa se rechaza
 * y el store decide (deja pasar con `console.error`, sin recordar).
 *
 * Los campos llegan como `unknown` y se refinan aquí: los escribe otro repo.
 * Contratos: `specs/API-CONTRACT/legaldocs-current-version.md` (réplica v1) y
 * `specs/API-CONTRACT/users.md` (réplica v2).
 */
export class FirestoreLegalConsentRepository implements ILegalConsentRepository {
  async getCurrentVersion(): Promise<CurrentLegalVersion | null> {
    const data = await getDocumentFromServer<LegalDocsRaw>('legaldocs_current_version', 'current')
    if (!data) return null
    const version = data.version_no
    if (typeof version !== 'string' || version.trim() === '') return null
    return {
      version_no: version,
      description: typeof data.description === 'string' ? data.description : '',
    }
  }

  async getUserLegalState(uid: string): Promise<UserLegalState> {
    const data = await getDocumentFromServer<UserLegalRaw>('users', uid)
    if (!data) return { exists: false, acceptedVersion: null, displayName: null }
    const accepted = data.accepted_legal_version
    return {
      exists: true,
      acceptedVersion: typeof accepted === 'string' && accepted !== '' ? accepted : null,
      displayName: typeof data.display_name === 'string' ? data.display_name : null,
    }
  }
}
